import React, { useState, useEffect } from 'react';
import { Progress } from '@/components/ui/progress.tsx';
import { CheckCircle, X } from 'lucide-react';
import { Button } from '@/components/ui/button.tsx';

interface TransferStatusProps {
  transferId: number | null;
  transferState: 'idle' | 'waiting' | 'accepted';
  careSpecialist: string;
  onClose: (isCancellation?: boolean) => void;
  showNextPatientText?: boolean; // New prop to control showing "Next Patient" text
}

export const TransferStatus: React.FC<TransferStatusProps> = ({
  transferId,
  transferState,
  careSpecialist,
  onClose,
  showNextPatientText = true // Default to true for backward compatibility
}) => {
  const [countdownTimer, setCountdownTimer] = useState(120); // 2 minutes in seconds
  const [transferProgress, setTransferProgress] = useState(0);

  // Countdown timer effect - only when waiting
  useEffect(() => {
    if (transferState === 'waiting' && countdownTimer > 0) {
      const timer = setTimeout(() => {
        setCountdownTimer(prev => {
          const newTime = prev - 1;
          // Calculate progress based on remaining time (starts at 100%, decreases to 0%)
          setTransferProgress((newTime / 120) * 100);
          return newTime;
        });
      }, 1000);
      return () => clearTimeout(timer);
    } else if (transferState === 'accepted') {
      // Stop the countdown timer when transfer is accepted
      setTransferProgress(100);
    }
  }, [countdownTimer, transferState]);

  // Reset timer when state changes to waiting
  useEffect(() => {
    if (transferState === 'waiting') {
      setCountdownTimer(120);
      setTransferProgress(100); // Start with full progress (100% remaining time)
    }
  }, [transferState]);

  // Reset timer when component is closed/reopened
  useEffect(() => {
    if (transferId) {
      setCountdownTimer(120);
      setTransferProgress(100); // Start with full progress (100% remaining time)
    }
  }, [transferId]);

  const formatCountdown = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  if (!transferId || transferState === 'idle') {
    console.log('TransferStatus: Component not rendering', { transferId, transferState });
    return null;
  }

  console.log('TransferStatus: Component rendering with state:', { transferState, transferId, careSpecialist });

  return (
    <div className="fixed bottom-4 right-4 bg-white rounded-lg shadow-lg border p-4 min-w-[300px] max-w-[400px] z-50">


      {transferState === 'waiting' && (
        <>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-gray-900">
              Waiting for Care Specialist Approval
            </h3>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600 font-medium">
                {formatCountdown(countdownTimer)}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onClose(true)} // true = cancellation
                className="h-6 w-6 p-0 text-gray-400 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <Progress 
            value={transferProgress} 
            className={`w-full mb-3 ${
              transferProgress <= 25 ? 'progress-danger' : 
              transferProgress <= 50 ? 'progress-warning' : 
              'progress-normal'
            }`} 
          />
          <p className="text-xs text-gray-600 leading-relaxed">
            This patient will be transferred to the Care Specialist once they accepts the transfer request.
          </p>
        </>
      )}

      {transferState === 'accepted' && (
        <div className="space-y-3">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-green-800">
              Transfer Accepted
            </h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onClose(false)} // false = not a cancellation, just manual close on success
              className="h-6 w-6 p-0 text-gray-400 hover:text-gray-600"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          <div className="flex items-center space-x-2 p-3 bg-green-50 rounded-lg">
            <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
            <span className="text-sm text-green-800">
              {careSpecialist} has accepted the Transfer Request
            </span>
          </div>
          {showNextPatientText && (
            <p className="text-sm text-blue-600 font-medium">
              Click on "Next Patient" Button to Proceed
            </p>
          )}
        </div>
      )}
    </div>
  );
};